/*!
 * BOPF storefront signup popup   [v1 - 2026-05-18]
 *
 * A two-step popup that replaces the Bitespeed signup form.
 *   Step 1: first name + WhatsApp number (+ WhatsApp consent)  -> Continue
 *   Step 2: email (+ email consent)                            -> Join the list
 *
 * Completing step 1 writes the WhatsApp opt-in to the consent store
 * immediately, so a step-2 drop-off still leaves a real WhatsApp lead.
 * Step 2 adds the email to the Shopify customer's marketing consent.
 *
 * Posts to /apps/bopf/signup via the Shopify App Proxy (same pattern as
 * the Zara chat widget at /apps/bopf/chat) - no CORS, same origin.
 *
 * Install:
 *   1. Save this file in your theme as: assets/bopf-signup.js
 *   2. In layout/theme.liquid, just before </body>:
 *        <script src="{{ 'bopf-signup.js' | asset_url }}?v=1" defer></script>
 *      Bump ?v=1 to ?v=2, ?v=3... each time you push a new version, so
 *      Shopify's CDN serves the fresh file.
 *
 * Testing helpers (run in the browser console on the storefront):
 *   bopfSignupShow()   - force the popup open, ignoring suppression
 *   bopfSignupReset()  - clear all frequency-capping state
 */

(function () {
  'use strict';

  console.log('[BOPF signup v1] script parsed');

  // ---- Configuration --------------------------------------------------
  var CONFIG = {
    endpoint:               '/apps/bopf/signup',
    timeDelayMs:            28000,   // time-on-page trigger
    scrollDepthPct:         50,      // scroll-depth trigger
    behaviouralPageviews:   2,       // 2nd+ pageview in the session
    behaviouralDwellMs:     8000,    // ...then 8s of dwell
    minTimeBeforeShowMs:    6000,    // never show in the first 6s of a load
    suppressDays:           30,      // cooldown after a show / dismiss
    storageKey:             'bopf_signup_v1',
    pvKey:                  'bopf_signup_pv'
  };

  // Country dial codes for the phone field. UAE first (default).
  var COUNTRIES = [
    { code: '+971', label: '+971' },
    { code: '+966', label: '+966' },
    { code: '+965', label: '+965' },
    { code: '+974', label: '+974' },
    { code: '+973', label: '+973' },
    { code: '+968', label: '+968' },
    { code: '+44',  label: '+44'  },
    { code: '+1',   label: '+1'   },
    { code: '+91',  label: '+91'  },
    { code: '+92',  label: '+92'  },
    { code: '+20',  label: '+20'  }
  ];

  // ---- Frequency-capping state (localStorage) -------------------------
  function getState() {
    try { return JSON.parse(localStorage.getItem(CONFIG.storageKey) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function setState(s) {
    try { localStorage.setItem(CONFIG.storageKey, JSON.stringify(s)); } catch (e) {}
  }
  function markShown(reason) {
    var s = getState(); s.shownAt = Date.now(); s.lastReason = reason; setState(s);
  }
  function markDismissed() {
    var s = getState(); s.dismissedAt = Date.now(); setState(s);
  }
  function markSignedUp() {
    var s = getState(); s.signedUp = true; s.signedUpAt = Date.now(); setState(s);
  }

  // shouldSuppress: true when the popup must NOT show.
  function shouldSuppress() {
    var s = getState();
    var win = CONFIG.suppressDays * 86400000;
    if (s.signedUp) return true;                                    // completed -> never again
    if (s.shownAt && (Date.now() - s.shownAt) < win) return true;   // shown -> 30d cooldown
    if (s.dismissedAt && (Date.now() - s.dismissedAt) < win) return true;
    var path = (location.pathname || '').toLowerCase();
    if (path.indexOf('/checkout') !== -1) return true;              // never during checkout
    return false;
  }

  function isTouch() {
    return ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0);
  }

  // ---- Styles ---------------------------------------------------------
  var CHEVRON = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='10'%20height='6'%20viewBox='0%200%2010%206'%3E%3Cpath%20d='M1%201l4%204%204-4'%20stroke='%239A9183'%20stroke-width='1.5'%20fill='none'%20stroke-linecap='round'%20stroke-linejoin='round'/%3E%3C/svg%3E";

  function injectStyles() {
    if (document.getElementById('bopf-su-styles')) return;
    var css = [
      '#bopf-su-ov{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(20,18,15,0.55);opacity:0;transition:opacity .28s ease;font-family:"proxima-nova",system-ui,sans-serif;-webkit-tap-highlight-color:transparent}',
      '#bopf-su-ov.bopf-su-show{opacity:1}',
      '#bopf-su-card{position:relative;width:440px;max-width:100%;background:#FBF3EE;border:1px solid #EFE1D5;border-radius:4px;padding:34px 34px 28px;box-sizing:border-box;transform:translateY(12px) scale(.985);transition:transform .3s cubic-bezier(.16,1,.3,1);max-height:calc(100vh - 40px);overflow-y:auto}',
      '#bopf-su-ov.bopf-su-show #bopf-su-card{transform:none}',
      '#bopf-su-card *{box-sizing:border-box}',
      '.bopf-su-close{position:absolute;top:12px;right:12px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;background:none;border:none;cursor:pointer;color:#A89A88;font-size:21px;line-height:1;padding:0;z-index:2}',
      '.bopf-su-close:hover{color:#2B1F1A}',
      '.bopf-su-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-right:26px;min-height:18px}',
      '.bopf-su-kicker{font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#B86842}',
      '.bopf-su-count{font-size:10.5px;letter-spacing:.18em;color:#B0A290}',
      '.bopf-su-back{display:flex;align-items:center;gap:5px;background:none;border:none;cursor:pointer;font:inherit;font-size:11px;color:#A89A88;padding:0}',
      '.bopf-su-back:hover{color:#2B1F1A}',
      '.bopf-su-back svg{display:block}',
      '.bopf-su-h{font-family:"Fraunces",Georgia,serif;font-size:23px;line-height:1.3;color:#2B1F1A;margin:0 0 11px;white-space:nowrap}',
      '.bopf-su-word{position:relative;display:inline-block}',
      '.bopf-su-word svg{position:absolute;left:0;bottom:-7px;width:100%;height:9px;overflow:visible}',
      '.bopf-su-sub{font-size:13.5px;line-height:1.6;color:#8A7868;margin:0 0 18px}',
      '.bopf-su-perk{display:flex;align-items:center;justify-content:center;gap:11px;margin-bottom:22px}',
      '.bopf-su-perk i{font-family:"Fraunces",Georgia,serif;font-style:italic;font-size:13px;color:#B86842;white-space:nowrap}',
      '.bopf-su-field{margin-bottom:13px}',
      '.bopf-su-label{display:block;font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#9A8A78;margin-bottom:6px}',
      '.bopf-su-input{width:100%;height:44px;background:#fff;border:1px solid #E4D6C7;border-radius:3px;padding:0 12px;font:inherit;font-size:14px;color:#2B1F1A;-webkit-appearance:none;appearance:none}',
      '.bopf-su-input:focus{outline:none;border-color:#B86842}',
      '.bopf-su-input::placeholder{color:#BCAE9C}',
      '.bopf-su-phone{display:flex}',
      '.bopf-su-cc{height:44px;background:#fff;border:1px solid #E4D6C7;border-right:none;border-radius:3px 0 0 3px;padding:0 24px 0 11px;font:inherit;font-size:14px;color:#2B1F1A;-webkit-appearance:none;appearance:none;cursor:pointer;background-image:url("' + CHEVRON + '");background-repeat:no-repeat;background-position:right 9px center}',
      '.bopf-su-cc:focus{outline:none;border-color:#B86842}',
      '.bopf-su-phone .bopf-su-input{border-radius:0 3px 3px 0}',
      '.bopf-su-consent{display:flex;align-items:flex-start;gap:9px;margin:4px 0 16px;cursor:pointer}',
      '.bopf-su-consent input{flex-shrink:0;width:16px;height:16px;margin:1px 0 0;accent-color:#B86842;cursor:pointer}',
      '.bopf-su-consent span{font-size:11.5px;line-height:1.5;color:#9A8A78}',
      '.bopf-su-btn{width:100%;height:46px;background:#2B1F1A;color:#FBF3EE;border:none;border-radius:3px;font:inherit;font-size:12px;letter-spacing:.12em;text-transform:uppercase;cursor:pointer;transition:background .15s}',
      '.bopf-su-btn:hover{background:#3a2c23}',
      '.bopf-su-btn:disabled{opacity:.55;cursor:default}',
      '.bopf-su-skip{display:block;width:100%;text-align:center;margin-top:13px;background:none;border:none;cursor:pointer;font:inherit;font-size:11.5px;color:#A89A88;text-decoration:underline}',
      '.bopf-su-skip:hover{color:#2B1F1A}',
      '.bopf-su-err{font-size:11.5px;line-height:1.4;color:#B0492B;margin:-4px 0 12px;display:none}',
      '.bopf-su-err.bopf-su-on{display:block}',
      '.bopf-su-success{text-align:center;padding:18px 6px 10px}',
      '.bopf-su-success .bopf-su-h{white-space:normal;margin-bottom:9px}',
      '.bopf-su-success p{font-size:13.5px;line-height:1.6;color:#8A7868;margin:0}',
      '@media(max-width:520px){#bopf-su-card{padding:30px 22px 24px}.bopf-su-h{font-size:18px}.bopf-su-sub{font-size:13px}}'
    ].join('');
    var st = document.createElement('style');
    st.id = 'bopf-su-styles';
    st.textContent = css;
    document.head.appendChild(st);
  }

  // ---- Markup ---------------------------------------------------------
  // A wavy underline scribble that stretches to the width of its word.
  // vector-effect keeps the stroke crisp despite non-uniform scaling.
  function scribble() {
    return '<svg viewBox="0 0 120 10" preserveAspectRatio="none" aria-hidden="true">' +
      '<path d="M2 6 Q12 2 22 6 T42 6 T62 6 T82 6 T102 6 T118 5" stroke="#B86842" ' +
      'stroke-width="1.6" fill="none" stroke-linecap="round" vector-effect="non-scaling-stroke"/></svg>';
  }
  function flourish(up) {
    var d = up ? 'M2 4 Q9 1 16 4 T30 4 T44 4' : 'M2 4 Q9 7 16 4 T30 4 T44 4';
    return '<svg width="46" height="8" viewBox="0 0 46 8" aria-hidden="true">' +
      '<path d="' + d + '" stroke="#B86842" stroke-width="1.3" fill="none" stroke-linecap="round"/></svg>';
  }
  function backArrow() {
    return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M14 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round"/></svg>';
  }

  function countryOptions() {
    var out = '';
    for (var i = 0; i < COUNTRIES.length; i++) {
      out += '<option value="' + COUNTRIES[i].code + '">' + COUNTRIES[i].label + '</option>';
    }
    return out;
  }

  function cardHTML() {
    return '' +
      '<button class="bopf-su-close" type="button" aria-label="Close">&#215;</button>' +

      // ---- Step 1: first name + WhatsApp ----
      '<div id="bopf-su-step1">' +
        '<div class="bopf-su-top">' +
          '<div class="bopf-su-kicker">The BOPF List</div>' +
          '<div class="bopf-su-count">01 / 02</div>' +
        '</div>' +
        '<h2 class="bopf-su-h">First dibs on <span class="bopf-su-word">everything' +
          scribble() + '</span>.</h2>' +
        '<p class="bopf-su-sub">New-in luxury pieces and private sale access, sent ' +
          'straight to your WhatsApp before they sell out.</p>' +
        '<div class="bopf-su-perk">' + flourish(true) +
          '<i>Early access, before everyone else</i>' + flourish(false) + '</div>' +
        '<div class="bopf-su-field">' +
          '<label class="bopf-su-label" for="bopf-su-name">First name</label>' +
          '<input class="bopf-su-input" id="bopf-su-name" type="text" ' +
            'autocomplete="given-name" placeholder="Hadiza">' +
        '</div>' +
        '<div class="bopf-su-field">' +
          '<label class="bopf-su-label" for="bopf-su-phone">WhatsApp number</label>' +
          '<div class="bopf-su-phone">' +
            '<select class="bopf-su-cc" id="bopf-su-cc" aria-label="Country code">' +
              countryOptions() + '</select>' +
            '<input class="bopf-su-input" id="bopf-su-phone" type="tel" ' +
              'autocomplete="tel-national" placeholder="50 123 4567">' +
          '</div>' +
        '</div>' +
        '<label class="bopf-su-consent">' +
          '<input type="checkbox" id="bopf-su-c1">' +
          '<span>Yes, send me new arrivals and private sale access on WhatsApp. ' +
            'I can reply STOP anytime.</span>' +
        '</label>' +
        '<div class="bopf-su-err" id="bopf-su-err1"></div>' +
        '<button class="bopf-su-btn" id="bopf-su-go1" type="button">Continue</button>' +
        '<button class="bopf-su-skip" id="bopf-su-no" type="button">No thanks</button>' +
      '</div>' +

      // ---- Step 2: email ----
      '<div id="bopf-su-step2" style="display:none">' +
        '<div class="bopf-su-top">' +
          '<button class="bopf-su-back" id="bopf-su-back" type="button">' +
            backArrow() + ' Back</button>' +
          '<div class="bopf-su-count">02 / 02</div>' +
        '</div>' +
        '<h2 class="bopf-su-h">One last <span class="bopf-su-word">thing' +
          scribble() + '</span>.</h2>' +
        '<p class="bopf-su-sub">Add your email and we will send the lookbook and ' +
          'longer stories there too, alongside your WhatsApp alerts.</p>' +
        '<div class="bopf-su-field">' +
          '<label class="bopf-su-label" for="bopf-su-email">Email address</label>' +
          '<input class="bopf-su-input" id="bopf-su-email" type="email" ' +
            'autocomplete="email" placeholder="you@example.com">' +
        '</div>' +
        '<label class="bopf-su-consent">' +
          '<input type="checkbox" id="bopf-su-c2" checked>' +
          '<span>Yes, email me new arrivals, offers and private sale access. ' +
            'I can unsubscribe anytime.</span>' +
        '</label>' +
        '<div class="bopf-su-err" id="bopf-su-err2"></div>' +
        '<button class="bopf-su-btn" id="bopf-su-go2" type="button">Join the list</button>' +
        '<button class="bopf-su-skip" id="bopf-su-skip" type="button">' +
          'Skip, WhatsApp is enough</button>' +
      '</div>' +

      // ---- Success ----
      '<div id="bopf-su-success" style="display:none">' +
        '<div class="bopf-su-success">' +
          '<h2 class="bopf-su-h">You are on the <span class="bopf-su-word">list' +
            scribble() + '</span>.</h2>' +
          '<p>Keep an eye on your WhatsApp. The good pieces go fast.</p>' +
        '</div>' +
      '</div>';
  }

  // ---- Popup lifecycle ------------------------------------------------
  var overlay = null;
  var escHandler = null;

  function openPopup(reason) {
    if (overlay || document.getElementById('bopf-su-ov')) return;
    injectStyles();

    overlay = document.createElement('div');
    overlay.id = 'bopf-su-ov';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Join the BOPF List');
    overlay.innerHTML = '<div id="bopf-su-card">' + cardHTML() + '</div>';
    document.body.appendChild(overlay);

    markShown(reason);
    wireEvents();

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { overlay.classList.add('bopf-su-show'); });
    });
    setTimeout(function () {
      var f = document.getElementById('bopf-su-name');
      if (f) { try { f.focus(); } catch (e) {} }
    }, 340);
  }

  function closePopup(wasDismiss) {
    if (!overlay) return;
    if (wasDismiss) markDismissed();
    if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
    overlay.classList.remove('bopf-su-show');
    var ref = overlay;
    overlay = null;
    setTimeout(function () {
      if (ref && ref.parentNode) ref.parentNode.removeChild(ref);
    }, 320);
  }

  function showErr(el, msg) { if (el) { el.textContent = msg; el.classList.add('bopf-su-on'); } }
  function hideErr(el) { if (el) { el.classList.remove('bopf-su-on'); el.textContent = ''; } }

  function postSignup(data) {
    return fetch(CONFIG.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) {
      return r.json().catch(function () { return { ok: r.ok }; });
    });
  }

  function wireEvents() {
    var card    = document.getElementById('bopf-su-card');
    var step1   = document.getElementById('bopf-su-step1');
    var step2   = document.getElementById('bopf-su-step2');
    var success = document.getElementById('bopf-su-success');

    var nameInput  = document.getElementById('bopf-su-name');
    var ccSelect   = document.getElementById('bopf-su-cc');
    var phoneInput = document.getElementById('bopf-su-phone');
    var consent1   = document.getElementById('bopf-su-c1');
    var emailInput = document.getElementById('bopf-su-email');
    var err1       = document.getElementById('bopf-su-err1');
    var err2       = document.getElementById('bopf-su-err2');
    var go1        = document.getElementById('bopf-su-go1');
    var go2        = document.getElementById('bopf-su-go2');

    // Dismiss: close button, "No thanks", backdrop click, Escape.
    card.querySelector('.bopf-su-close').addEventListener('click', function () {
      closePopup(true);
    });
    document.getElementById('bopf-su-no').addEventListener('click', function () {
      closePopup(true);
    });
    overlay.addEventListener('mousedown', function (e) {
      if (e.target === overlay) closePopup(true);
    });
    escHandler = function (e) { if (e.key === 'Escape' || e.keyCode === 27) closePopup(true); };
    document.addEventListener('keydown', escHandler);

    // Step 2 -> back to step 1.
    document.getElementById('bopf-su-back').addEventListener('click', function () {
      step2.style.display = 'none';
      step1.style.display = '';
    });

    function goStep2() {
      step1.style.display = 'none';
      step2.style.display = '';
      setTimeout(function () { try { emailInput.focus(); } catch (e) {} }, 60);
    }

    function showSuccess() {
      step1.style.display = 'none';
      step2.style.display = 'none';
      success.style.display = '';
      setTimeout(function () { closePopup(false); }, 2800);
    }

    // ---- Step 1 submit ----
    function submitStep1() {
      var name   = (nameInput.value || '').trim();
      var digits = (phoneInput.value || '').replace(/\D/g, '').replace(/^0+/, '');
      if (!name) { showErr(err1, 'Please enter your first name'); return; }
      if (digits.length < 7) { showErr(err1, 'Please enter a valid WhatsApp number'); return; }
      if (!consent1.checked) { showErr(err1, 'Please tick the box so we can message you'); return; }
      hideErr(err1);
      go1.disabled = true; go1.textContent = 'Saving...';
      postSignup({
        step: 1,
        first_name: name,
        phone: ccSelect.value + digits,
        consent: true,
        source: 'website_popup',
        page_url: location.href
      }).then(function (r) {
        if (r && r.ok) {
          markSignedUp();   // completing step 1 IS the signup
          goStep2();
        } else {
          showErr(err1, (r && r.error) || 'Something went wrong. Please try again.');
          go1.disabled = false; go1.textContent = 'Continue';
        }
      }).catch(function () {
        showErr(err1, 'Network error. Please try again.');
        go1.disabled = false; go1.textContent = 'Continue';
      });
    }

    // ---- Step 2 submit ----
    function submitStep2() {
      var email = (emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showErr(err2, 'Please enter a valid email address'); return;
      }
      hideErr(err2);
      go2.disabled = true; go2.textContent = 'Saving...';
      // Step 1 already locked them in, so a step-2 failure never blocks
      // the visitor - either outcome lands on the success screen.
      postSignup({ step: 2, email: email, first_name: (nameInput.value || '').trim() })
        .then(function () { showSuccess(); })
        .catch(function () { showSuccess(); });
    }

    go1.addEventListener('click', submitStep1);
    go2.addEventListener('click', submitStep2);
    document.getElementById('bopf-su-skip').addEventListener('click', showSuccess);

    // Enter key submits the visible step.
    card.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      if (step1.style.display !== 'none') { e.preventDefault(); submitStep1(); }
      else if (step2.style.display !== 'none') { e.preventDefault(); submitStep2(); }
    });
  }

  // ---- Triggers -------------------------------------------------------
  var pageLoadedAt = Date.now();
  var armed = true;
  var listeners = [];
  var timeTimer = null;
  var behavTimer = null;

  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    listeners.push({ t: target, ty: type, fn: fn });
  }
  function disarm() {
    armed = false;
    listeners.forEach(function (l) {
      try { l.t.removeEventListener(l.ty, l.fn); } catch (e) {}
    });
    listeners = [];
    if (timeTimer) { clearTimeout(timeTimer); timeTimer = null; }
    if (behavTimer) { clearTimeout(behavTimer); behavTimer = null; }
  }

  function fireTrigger(reason) {
    if (!armed) return;
    if (Date.now() - pageLoadedAt < CONFIG.minTimeBeforeShowMs) return;
    if (shouldSuppress()) { disarm(); return; }
    disarm();
    openPopup(reason);
  }

  function armTriggers() {
    // Exit-intent (desktop only - the cursor leaving toward the top).
    if (!isTouch() && window.innerWidth > 700) {
      on(document, 'mouseout', function (e) {
        if (e.clientY <= 2 && !e.relatedTarget && !e.toElement) fireTrigger('exit_intent');
      });
    }
    // Scroll depth.
    on(window, 'scroll', function () {
      var st = window.pageYOffset || document.documentElement.scrollTop || 0;
      var dh = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      if (dh > 0 && (st / dh) * 100 >= CONFIG.scrollDepthPct) fireTrigger('scroll_depth');
    }, { passive: true });
    // Time on page.
    timeTimer = setTimeout(function () { fireTrigger('time_delay'); }, CONFIG.timeDelayMs);
    // Behavioural intent: a 2nd+ pageview this session, then a short dwell.
    var pv = 0;
    try { pv = parseInt(sessionStorage.getItem(CONFIG.pvKey) || '0', 10) || 0; } catch (e) {}
    pv += 1;
    try { sessionStorage.setItem(CONFIG.pvKey, String(pv)); } catch (e) {}
    if (pv >= CONFIG.behaviouralPageviews) {
      behavTimer = setTimeout(function () { fireTrigger('behavioural'); }, CONFIG.behaviouralDwellMs);
    }
  }

  // ---- Init -----------------------------------------------------------
  function init() {
    // Manual testing hooks - always available.
    window.bopfSignupShow = function () {
      disarm();
      if (overlay) closePopup(false);
      setTimeout(function () { openPopup('manual'); }, 60);
    };
    window.bopfSignupReset = function () {
      try { localStorage.removeItem(CONFIG.storageKey); } catch (e) {}
      try { sessionStorage.removeItem(CONFIG.pvKey); } catch (e) {}
      console.log('[BOPF signup] state cleared');
    };

    if (shouldSuppress()) {
      console.log('[BOPF signup] suppressed for this load');
      return;
    }
    armTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
